import { ActivityLogStorageService } from './activityLogStorageService';
import { ActivityLogRequest, ActivityService } from './activityService';
import { CredentialsService } from './credentialsService';

export type DualWriteStatus =
  | { ok: true }
  | { ok: false; error: string };

export interface ActivityDualWriteResult {
  local: DualWriteStatus & { id?: string };
  caredaily: DualWriteStatus;
}

export interface ActivityDualWriteInput {
  facilityId: string;
  residentId: string; // CareDaily locationId, but stored as text locally
  externalId: string; // required for CareDaily log endpoint
  activityText: string;
  dimensionIds: string[]; // UUIDs (local)
  dimensionNames: string[]; // CareDaily LogCategory
}

/**
 * Orchestrates "dual write" activity logging:
 * - Always attempts to persist locally to Supabase `activity_logs`
 * - Also attempts to log to CareDaily `espapi/integration/goicon/logActivity`
 *
 * Both writes are best-effort. Caller can decide how to surface partial failure.
 */
export async function submitActivityDualWrite(
  input: ActivityDualWriteInput,
  options?: { envOverride?: 'sandbox' | 'production' }
): Promise<ActivityDualWriteResult> {
  // 1) Local-first (safety mechanism)
  let local: ActivityDualWriteResult['local'];
  try {
    const record = await ActivityLogStorageService.createActivityLog({
      facilityId: input.facilityId,
      residentId: input.residentId,
      activityText: input.activityText,
      dimensionIds: input.dimensionIds,
    });
    local = { ok: true, id: record.id };
  } catch (e) {
    const msg = e instanceof Error ? e.message : 'Unknown local storage error';
    local = { ok: false, error: msg };
  }

  // 2) Upstream CareDaily logging via proxy (ESP key handled server-side)
  let caredaily: ActivityDualWriteResult['caredaily'];
  try {
    let envOverride: 'sandbox' | 'production' | undefined = options?.envOverride;
    try {
      if (!envOverride) {
        const credentials = await CredentialsService.getCredentialStatus(input.facilityId);
        if (credentials) {
          envOverride = credentials.isSandbox ? 'sandbox' : 'production';
        }
      }
    } catch (e) {
      console.warn('⚠️ Unable to resolve CareDaily env for activity logging, using server default.', e);
    }

    const activityData: ActivityLogRequest = {
      LogCategory: input.dimensionNames,
      LogDetail: input.activityText,
      ResidentId: parseInt(input.residentId, 10),
      ExternalId: parseInt(input.externalId, 10),
    };

    const validationError = ActivityService.validateActivityData(activityData);
    if (validationError) {
      caredaily = { ok: false, error: validationError };
    } else {
      await ActivityService.logActivity(activityData, { envOverride });
      caredaily = { ok: true };
    }
  } catch (e) {
    const msg = e instanceof Error ? e.message : 'Unknown CareDaily logging error';
    caredaily = { ok: false, error: msg };
  }

  return { local, caredaily };
}

